import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';

import style from './MenuTrigger.module.css';

import { useHtmlElement, useViewport } from '../../../hooks';
import { Arrow } from '../../../icons/Arrow';
import { AppNavHeight } from '../../AppNav/DefaultAppNav/constants';
import { Button } from '../../Button';
import { Flex } from '../../Flex';
import { Menu } from './Menu';

export type MenuTriggerProps = {
  menuItems: { label: string; to: string }[];
  label: string;
  className: string;
};

export function MenuTrigger(props: MenuTriggerProps) {
  const [expanded, setExpanded] = useState(false);
  const [containerEl, containerRef] = useHtmlElement();
  const [portalEl, portalRef] = useHtmlElement();
  const [mobileMenuEl, mobileMenuRef] = useHtmlElement();
  const viewport = useViewport();

  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const handleClick = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const { menuItems } = props;

  const calculateMenuPosition = useCallback(() => {
    if (!containerEl) {
      return;
    }
    const menuTriggerPosition = containerEl.getBoundingClientRect();

    setMenuPosition({
      top: menuTriggerPosition.bottom + AppNavHeight + window.scrollY,
      left: menuTriggerPosition.left + window.scrollX,
    });
  }, [containerEl]);

  useLayoutEffect(() => {
    calculateMenuPosition();
    window.addEventListener('resize', calculateMenuPosition);
    return () => {
      window.removeEventListener('resize', calculateMenuPosition);
    };
  }, [calculateMenuPosition]);

  useEffect(() => {
    setExpanded(false);
  }, [viewport.lg]);

  useEffect(() => {
    if (!expanded || !containerEl || !viewport.lg) {
      return;
    }

    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Element;
      if (
        !containerEl.contains(target) &&
        !portalEl?.contains(target) &&
        !mobileMenuEl?.contains(target)
      ) {
        setExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [expanded, containerEl, portalEl, mobileMenuEl, viewport.lg]);

  return (
    <>
      <div
        className={classNames(style.menuTrigger, props.className)}
        ref={containerRef}
      >
        <Button
          variant="plain-text"
          color="kale-dark"
          className={style.menuTriggerButton}
          aria-haspopup
          aria-expanded={expanded}
          onClick={handleClick}
        >
          <Flex alignItems="center" gap={12}>
            {props.label}
            <Arrow
              aria-hidden
              arrowDirection={expanded ? 'up' : 'down'}
              size={17}
            />
          </Flex>
        </Button>
      </div>
      <div className={style.popup}>
        {createPortal(
          <div
            ref={portalRef}
            style={menuPosition}
            className={classNames(style.popup, { [style.hidden]: !expanded })}
          >
            <Menu label={props.label} menuItems={menuItems} />
          </div>,
          document.body
        )}
      </div>
      <div
        ref={mobileMenuRef}
        className={classNames(style.mobileMenu, { [style.hidden]: !expanded })}
      >
        <Menu label={props.label} menuItems={menuItems} />
      </div>
    </>
  );
}
